import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { 
  ChevronRight, ChevronLeft, Check, X, Ruler, Activity, Brain, 
  Utensils, MessageCircle, Target, Bell, User, Dumbbell, Heart
} from 'lucide-react';
import { toast } from 'sonner';
import { BACKEND_URL } from '../config/api';

const API = `${BACKEND_URL}/api`;

const ICON_MAP = {
  ruler: Ruler,
  activity: Activity,
  brain: Brain,
  utensils: Utensils,
  'message-circle': MessageCircle
};

export default function OnboardingWizard({ onComplete, onSkip }) {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  
  // Form data
  const [formData, setFormData] = useState({
    profile: {
      first_name: '',
      last_name: '',
      phone: '',
      organization: '',
      role_description: ''
    },
    sports: {
      primary_sport: '',
      other_sports: [],
      experience_level: '',
      training_frequency: ''
    },
    goals: {
      goals: [],
      focus_areas: []
    },
    notifications: {
      email_notifications: true,
      weekly_reports: true,
      achievement_alerts: true,
      training_reminders: false
    }
  });

  useEffect(() => {
    loadConfig();
  }, []);

  const loadConfig = async () => {
    try {
      const response = await axios.get(`${API}/onboarding/config`);
      setConfig(response.data);
    } catch (err) {
      console.error('Failed to load onboarding config:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleNext = async () => {
    // Save progress
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/onboarding/save-progress`, 
        { step: step + 1, data: formData },
        { headers: { Authorization: `Bearer ${token}` } }
      );
    } catch (err) {
      console.error('Failed to save progress:', err);
    }
    
    if (step < 4) {
      setStep(step + 1);
    } else {
      handleComplete();
    }
  };

  const handleBack = () => {
    if (step > 0) {
      setStep(step - 1);
    }
  };

  const handleComplete = async () => {
    setSubmitting(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/onboarding/complete`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Welcome to BE ELITE APP!');
      onComplete?.();
      navigate('/');
    } catch (err) {
      toast.error('Failed to complete onboarding');
    } finally {
      setSubmitting(false);
    }
  };

  const handleSkip = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/onboarding/skip`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      onSkip?.();
      navigate('/');
    } catch (err) {
      navigate('/');
    }
  };

  const toggleGoal = (goalId) => {
    setFormData(prev => ({
      ...prev,
      goals: {
        ...prev.goals,
        goals: prev.goals.goals.includes(goalId)
          ? prev.goals.goals.filter(g => g !== goalId)
          : [...prev.goals.goals, goalId]
      }
    }));
  };

  const toggleFocusArea = (areaId) => {
    setFormData(prev => ({
      ...prev,
      goals: {
        ...prev.goals,
        focus_areas: prev.goals.focus_areas.includes(areaId)
          ? prev.goals.focus_areas.filter(a => a !== areaId)
          : [...prev.goals.focus_areas, areaId]
      }
    }));
  };

  const toggleSport = (sport) => {
    setFormData(prev => ({
      ...prev,
      sports: {
        ...prev.sports,
        other_sports: prev.sports.other_sports.includes(sport)
          ? prev.sports.other_sports.filter(s => s !== sport)
          : [...prev.sports.other_sports, sport]
      }
    }));
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="text-zinc-500 font-mono animate-pulse">Loading...</div>
      </div>
    );
  }

  const steps = [
    { title: 'Welcome', subtitle: 'Tell us about yourself' },
    { title: 'Your Sport', subtitle: 'What sports are you involved in?' },
    { title: 'Your Goals', subtitle: 'What do you want to achieve?' },
    { title: 'App Features', subtitle: 'Discover what you can do' },
    { title: 'Stay Updated', subtitle: 'Notification preferences' }
  ];

  return (
    <div className="min-h-screen bg-[#050505] grid-texture">
      {/* Header */}
      <div className="border-b border-zinc-800 bg-zinc-950/50 backdrop-blur-sm">
        <div className="max-w-3xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-white uppercase tracking-tight">
                Welcome to BE ELITE APP
              </h1>
              <p className="text-sm text-zinc-500 mt-1">{steps[step].subtitle}</p>
            </div>
            <Button
              variant="ghost"
              onClick={handleSkip}
              className="text-zinc-500 hover:text-white"
              data-testid="skip-onboarding-btn"
            >
              Skip
              <X className="ml-2 h-4 w-4" />
            </Button>
          </div>
          
          {/* Progress bar */}
          <div className="mt-4 flex gap-2">
            {steps.map((s, i) => (
              <div
                key={i}
                className={`h-1 flex-1 rounded-full transition-colors ${
                  i <= step ? 'bg-[#02b5ff]' : 'bg-zinc-800'
                }`}
              />
            ))}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-3xl mx-auto px-6 py-12">
        {/* Step 0: Profile */}
        {step === 0 && (
          <div className="space-y-6" data-testid="onboarding-step-profile">
            <div className="text-center mb-8">
              <div className="w-20 h-20 bg-[#02b5ff]/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <User className="h-10 w-10 text-[#02b5ff]" />
              </div>
              <h2 className="text-2xl font-bold text-white">Let's get started</h2>
              <p className="text-zinc-400 mt-2">Tell us a bit about yourself</p>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm text-zinc-400 mb-2">First Name</label>
                <input
                  type="text"
                  value={formData.profile.first_name}
                  onChange={(e) => setFormData(prev => ({
                    ...prev,
                    profile: { ...prev.profile, first_name: e.target.value }
                  }))}
                  className="w-full bg-zinc-900 border border-zinc-700 rounded-lg px-4 py-3 text-white focus:border-[#02b5ff] focus:outline-none"
                  placeholder="John"
                  data-testid="onboarding-first-name"
                />
              </div>
              <div>
                <label className="block text-sm text-zinc-400 mb-2">Last Name</label>
                <input
                  type="text"
                  value={formData.profile.last_name}
                  onChange={(e) => setFormData(prev => ({
                    ...prev,
                    profile: { ...prev.profile, last_name: e.target.value }
                  }))}
                  className="w-full bg-zinc-900 border border-zinc-700 rounded-lg px-4 py-3 text-white focus:border-[#02b5ff] focus:outline-none"
                  placeholder="Smith"
                  data-testid="onboarding-last-name"
                />
              </div>
            </div>
            
            <div>
              <label className="block text-sm text-zinc-400 mb-2">Phone Number</label>
              <input
                type="tel"
                value={formData.profile.phone}
                onChange={(e) => setFormData(prev => ({
                  ...prev,
                  profile: { ...prev.profile, phone: e.target.value }
                }))}
                className="w-full bg-zinc-900 border border-zinc-700 rounded-lg px-4 py-3 text-white focus:border-[#02b5ff] focus:outline-none"
                placeholder="+44 7xxx xxx xxx"
                data-testid="onboarding-phone"
              />
            </div>
            
            <div>
              <label className="block text-sm text-zinc-400 mb-2">Organization / Club (Optional)</label>
              <input
                type="text"
                value={formData.profile.organization}
                onChange={(e) => setFormData(prev => ({
                  ...prev,
                  profile: { ...prev.profile, organization: e.target.value }
                }))}
                className="w-full bg-zinc-900 border border-zinc-700 rounded-lg px-4 py-3 text-white focus:border-[#02b5ff] focus:outline-none"
                placeholder="e.g., Sutton United FC"
                data-testid="onboarding-organization"
              />
            </div>
          </div>
        )}

        {/* Step 1: Sports */}
        {step === 1 && (
          <div className="space-y-6" data-testid="onboarding-step-sports">
            <div className="text-center mb-8">
              <div className="w-20 h-20 bg-[#02b5ff]/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <Dumbbell className="h-10 w-10 text-[#02b5ff]" />
              </div>
              <h2 className="text-2xl font-bold text-white">Your Sport</h2>
              <p className="text-zinc-400 mt-2">Help us personalize your experience</p>
            </div>
            
            <div>
              <label className="block text-sm text-zinc-400 mb-3">Primary Sport</label>
              <div className="grid grid-cols-3 gap-3">
                {config?.sports?.slice(0, 12).map((sport) => (
                  <button
                    key={sport}
                    onClick={() => setFormData(prev => ({
                      ...prev,
                      sports: { ...prev.sports, primary_sport: sport }
                    }))}
                    className={`p-3 rounded-lg border text-sm font-medium transition-all ${
                      formData.sports.primary_sport === sport
                        ? 'bg-[#02b5ff]/20 border-[#02b5ff] text-[#02b5ff]'
                        : 'bg-zinc-900 border-zinc-700 text-zinc-300 hover:border-zinc-500'
                    }`}
                    data-testid={`sport-${sport.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    {sport}
                  </button>
                ))}
              </div>
            </div>
            
            <div>
              <label className="block text-sm text-zinc-400 mb-3">Experience Level</label>
              <div className="grid grid-cols-2 gap-3">
                {config?.experience_levels?.map((level) => (
                  <button
                    key={level.id}
                    onClick={() => setFormData(prev => ({
                      ...prev,
                      sports: { ...prev.sports, experience_level: level.id }
                    }))}
                    className={`p-3 rounded-lg border text-sm font-medium transition-all ${
                      formData.sports.experience_level === level.id
                        ? 'bg-[#02b5ff]/20 border-[#02b5ff] text-[#02b5ff]'
                        : 'bg-zinc-900 border-zinc-700 text-zinc-300 hover:border-zinc-500'
                    }`}
                  >
                    {level.label}
                  </button>
                ))}
              </div>
            </div>
            
            <div>
              <label className="block text-sm text-zinc-400 mb-3">Training Frequency</label>
              <div className="grid grid-cols-2 gap-3">
                {config?.training_frequencies?.map((freq) => (
                  <button
                    key={freq.id}
                    onClick={() => setFormData(prev => ({
                      ...prev,
                      sports: { ...prev.sports, training_frequency: freq.id }
                    }))}
                    className={`p-3 rounded-lg border text-sm font-medium transition-all ${
                      formData.sports.training_frequency === freq.id
                        ? 'bg-[#02b5ff]/20 border-[#02b5ff] text-[#02b5ff]'
                        : 'bg-zinc-900 border-zinc-700 text-zinc-300 hover:border-zinc-500'
                    }`}
                  >
                    {freq.label}
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Step 2: Goals */}
        {step === 2 && (
          <div className="space-y-6" data-testid="onboarding-step-goals">
            <div className="text-center mb-8">
              <div className="w-20 h-20 bg-[#02b5ff]/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <Target className="h-10 w-10 text-[#02b5ff]" />
              </div>
              <h2 className="text-2xl font-bold text-white">Your Goals</h2>
              <p className="text-zinc-400 mt-2">What do you want to achieve?</p>
            </div>
            
            <div>
              <label className="block text-sm text-zinc-400 mb-3">Select your goals (choose multiple)</label>
              <div className="grid grid-cols-2 gap-3">
                {config?.goals?.map((goal) => (
                  <button
                    key={goal.id}
                    onClick={() => toggleGoal(goal.id)}
                    className={`p-4 rounded-lg border text-left transition-all ${
                      formData.goals.goals.includes(goal.id)
                        ? 'bg-[#02b5ff]/20 border-[#02b5ff]'
                        : 'bg-zinc-900 border-zinc-700 hover:border-zinc-500'
                    }`}
                  >
                    <span className="text-2xl">{goal.icon}</span>
                    <p className={`text-sm font-medium mt-2 ${
                      formData.goals.goals.includes(goal.id) ? 'text-[#02b5ff]' : 'text-zinc-300'
                    }`}>
                      {goal.label}
                    </p>
                  </button>
                ))}
              </div>
            </div>
            
            <div>
              <label className="block text-sm text-zinc-400 mb-3">Focus Areas (optional)</label>
              <div className="flex flex-wrap gap-2">
                {config?.focus_areas?.map((area) => (
                  <button
                    key={area.id}
                    onClick={() => toggleFocusArea(area.id)}
                    className={`px-4 py-2 rounded-full border text-sm font-medium transition-all ${
                      formData.goals.focus_areas.includes(area.id)
                        ? 'bg-[#02b5ff]/20 border-[#02b5ff] text-[#02b5ff]'
                        : 'bg-zinc-900 border-zinc-700 text-zinc-300 hover:border-zinc-500'
                    }`}
                  >
                    {area.label}
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Step 3: App Features */}
        {step === 3 && (
          <div className="space-y-6" data-testid="onboarding-step-features">
            <div className="text-center mb-8">
              <h2 className="text-2xl font-bold text-white">Discover What You Can Do</h2>
              <p className="text-zinc-400 mt-2">Here's what BE ELITE APP offers</p>
            </div>
            
            <div className="space-y-4">
              {config?.features?.map((feature, idx) => {
                const Icon = ICON_MAP[feature.icon] || Activity;
                return (
                  <div
                    key={idx}
                    className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-6 hover:border-[#02b5ff]/30 transition-all"
                  >
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 bg-[#02b5ff]/20 rounded-lg flex items-center justify-center flex-shrink-0">
                        <Icon className="h-6 w-6 text-[#02b5ff]" />
                      </div>
                      <div className="flex-1">
                        <h3 className="text-lg font-bold text-white">{feature.title}</h3>
                        <p className="text-sm text-zinc-400 mt-1">{feature.description}</p>
                        <div className="flex flex-wrap gap-2 mt-3">
                          {feature.highlights.map((highlight, i) => (
                            <span
                              key={i}
                              className="px-2 py-1 bg-zinc-800 rounded text-xs text-zinc-300"
                            >
                              {highlight}
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Step 4: Notifications */}
        {step === 4 && (
          <div className="space-y-6" data-testid="onboarding-step-notifications">
            <div className="text-center mb-8">
              <div className="w-20 h-20 bg-[#02b5ff]/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <Bell className="h-10 w-10 text-[#02b5ff]" />
              </div>
              <h2 className="text-2xl font-bold text-white">Stay Updated</h2>
              <p className="text-zinc-400 mt-2">Choose how you'd like to receive updates</p>
            </div>
            
            <div className="space-y-4">
              {[
                { key: 'email_notifications', label: 'Email Notifications', desc: 'Important updates and alerts via email' },
                { key: 'weekly_reports', label: 'Weekly Progress Reports', desc: 'Summary of athlete progress every week' },
                { key: 'achievement_alerts', label: 'Achievement Alerts', desc: 'Get notified when athletes reach milestones' },
                { key: 'training_reminders', label: 'Training Reminders', desc: 'Reminders for upcoming sessions' }
              ].map((item) => (
                <div
                  key={item.key}
                  className="flex items-center justify-between p-4 bg-zinc-900/50 border border-zinc-800 rounded-lg"
                >
                  <div>
                    <p className="text-white font-medium">{item.label}</p>
                    <p className="text-sm text-zinc-500">{item.desc}</p>
                  </div>
                  <button
                    onClick={() => setFormData(prev => ({
                      ...prev,
                      notifications: {
                        ...prev.notifications,
                        [item.key]: !prev.notifications[item.key]
                      }
                    }))}
                    className={`w-12 h-6 rounded-full transition-colors ${
                      formData.notifications[item.key] ? 'bg-[#02b5ff]' : 'bg-zinc-700'
                    }`}
                  >
                    <div
                      className={`w-5 h-5 bg-white rounded-full transition-transform ${
                        formData.notifications[item.key] ? 'translate-x-6' : 'translate-x-0.5'
                      }`}
                    />
                  </button>
                </div>
              ))}
            </div>
            
            <div className="bg-[#02b5ff]/10 border border-[#02b5ff]/30 rounded-xl p-6 mt-8">
              <div className="flex items-center gap-3">
                <Check className="h-6 w-6 text-[#02b5ff]" />
                <div>
                  <p className="text-white font-medium">You're all set!</p>
                  <p className="text-sm text-zinc-400">Click "Complete" to start using BE ELITE APP</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Navigation */}
        <div className="flex items-center justify-between mt-12 pt-6 border-t border-zinc-800">
          <Button
            variant="ghost"
            onClick={handleBack}
            disabled={step === 0}
            className={`${step === 0 ? 'invisible' : ''} text-zinc-400 hover:text-white`}
            data-testid="onboarding-back-btn"
          >
            <ChevronLeft className="mr-2 h-4 w-4" />
            Back
          </Button>
          
          <div className="text-sm text-zinc-500">
            Step {step + 1} of {steps.length}
          </div>
          
          <Button
            onClick={handleNext}
            disabled={submitting}
            className="bg-[#02b5ff] hover:bg-[#02b5ff]/90 text-white"
            data-testid="onboarding-next-btn"
          >
            {submitting ? 'Saving...' : step === 4 ? 'Complete' : 'Next'}
            {!submitting && step < 4 && <ChevronRight className="ml-2 h-4 w-4" />}
            {!submitting && step === 4 && <Check className="ml-2 h-4 w-4" />}
          </Button>
        </div>
      </div>
    </div>
  );
}
